import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "@/lib/translations";

const SUPPORTED = ["ro", "en", "tr"];

/**
 * Detect the user's language from timezone (no external API, no CORS).
 * - Europe/Bucharest → RO
 * - Europe/Istanbul → TR
 * - anything else / unknown → EN
 * Falls back to EN if the Intl API is not available.
 */
function detectFromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz === "Europe/Bucharest") return "ro";
    if (tz === "Europe/Istanbul") return "tr";
    return "en";
  } catch {
    return "en";
  }
}

const I18nContext = createContext({
  lang: "ro",
  setLang: () => {},
  t: (k) => k,
});

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "ro";
    const stored = localStorage.getItem("gc_lang");
    if (stored && SUPPORTED.includes(stored)) return stored;
    return detectFromTimezone();
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("gc_lang", lang);
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const t = useMemo(() => {
    return (key) => {
      const parts = key.split(".");
      let cur = translations[lang];
      for (const p of parts) {
        if (cur == null) return key;
        cur = cur[p];
      }
      if (cur == null) {
        // fallback to RO
        let fb = translations.ro;
        for (const p of parts) {
          if (fb == null) return key;
          fb = fb[p];
        }
        return fb ?? key;
      }
      return cur;
    };
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
